import { useAuthStore } from "@/stores"
import { Avatar, AvatarFallback, AvatarImage, Button } from "@/components/ui"
import { Menu, Wallet, Search } from "lucide-react"
import { Link } from "react-router-dom"
import { WALLET_1_NAME, WALLET_2_NAME, WALLET_DESCRIPTIONS, WALLET_RETENIDO_NAME } from "@/lib/wallets"

const WALLET_RETENIDO_TITULO = `${WALLET_RETENIDO_NAME}: ${WALLET_DESCRIPTIONS.wallet5}`

interface HeaderProps {
  onMenuClick?: () => void
  onCommandClick?: () => void
}

export function Header({ onMenuClick, onCommandClick }: HeaderProps) {
  const { user } = useAuthStore()

  const initials = user
    ? `${user.nombre?.charAt(0) || ""}${user.apellidos?.charAt(0) || ""}`.toUpperCase()
    : "?"

  const formatCurrency = (value: number | undefined) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(value || 0)
  }

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      {/* Left: Menu (mobile) + Buscar (command palette) */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" className="md:hidden" onClick={onMenuClick}>
          <Menu className="h-5 w-5" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 text-muted-foreground hover:text-foreground border-muted"
          onClick={onCommandClick}
        >
          <Search className="h-4 w-4" />
          <span className="hidden sm:inline">Buscar...</span>
          <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium sm:inline-flex">
            ⌘K
          </kbd>
        </Button>
      </div>

      {/* Center: Wallets info (pills branded)
       * - A/ADMIN crédito ilimitado: sin wallets propias.
       * - A fondeado (FamilyCel): solo wallet1 (no comisiona → wallet2 = 0).
       * - C: solo wallet1 (TA). No tiene comisiones.
       * - M: solo wallet2 (comisiones). No tiene tiempo aire (2026-10-01).
       * - B: wallet1 + wallet2.
       */}
      {user?.credito_ilimitado ? (
        <div className="hidden md:flex items-center gap-2 text-sm">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-primary font-semibold">
            <Wallet className="h-3.5 w-3.5" />
            Crédito ilimitado
          </span>
        </div>
      ) : (
        <div className="hidden md:flex items-center gap-2 text-sm">
          {user?.tipo_usuario !== "M" && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1"
              title={WALLET_1_NAME}
            >
              <Wallet className="h-3.5 w-3.5 text-primary" />
              <span className="text-muted-foreground text-xs">{WALLET_1_NAME}:</span>
              <span className="font-bold text-primary">{formatCurrency(user?.wallet1 ?? 0)}</span>
            </span>
          )}
          {user?.tipo_usuario !== "C" && user?.tipo_usuario !== "A" && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-3 py-1"
              title={WALLET_2_NAME}
            >
              <span className="text-muted-foreground text-xs">{WALLET_2_NAME}:</span>
              <span className="font-bold text-amber-600 dark:text-amber-500">{formatCurrency(user?.wallet2 ?? 0)}</span>
            </span>
          )}
          {/* Retenido: sólo aparece si hay algo retenido. Es dinero del usuario que NO
              puede gastar, así que esconderlo sería peor que un cero de más — pero
              mostrarlo siempre en $0.00 le daría presencia permanente a una excepción. */}
          {Number(user?.wallet5 ?? 0) > 0 && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/10 px-3 py-1"
              title={WALLET_RETENIDO_TITULO}
            >
              <span className="text-muted-foreground text-xs">{WALLET_RETENIDO_NAME}:</span>
              <span className="font-bold text-rose-600 dark:text-rose-400">{formatCurrency(user?.wallet5 ?? 0)}</span>
            </span>
          )}
        </div>
      )}

      {/* Mobile wallets - más compacto */}
      <div className="flex md:hidden items-center text-xs">
        {user?.credito_ilimitado ? (
          <span className="font-semibold text-primary">Crédito ∞</span>
        ) : (
          <div className="flex flex-col items-end">
            {user?.tipo_usuario !== "M" && (
              <span className="text-primary font-bold" title={WALLET_1_NAME}>{formatCurrency(user?.wallet1 ?? 0)}</span>
            )}
            {user?.tipo_usuario !== "C" && user?.tipo_usuario !== "A" && (
              <span className="text-amber-600 dark:text-amber-500" title={WALLET_2_NAME}>{formatCurrency(user?.wallet2 ?? 0)}</span>
            )}
            {Number(user?.wallet5 ?? 0) > 0 && (
              <span className="text-rose-600 dark:text-rose-400" title={WALLET_RETENIDO_TITULO}>{formatCurrency(user?.wallet5 ?? 0)}</span>
            )}
          </div>
        )}
      </div>

      {/* Right side: User */}
      <div className="flex items-center gap-3">
        <Link to="/perfil" className="flex items-center gap-3">
          <div className="hidden flex-col items-end md:flex">
            <span className="text-sm font-medium">
              {user?.pilas || user?.nombre}
            </span>
            <span className="text-xs text-muted-foreground">
              {user?.nombre} {user?.apellidos}
            </span>
          </div>
          <Avatar className="h-10 w-10 ring-2 ring-primary/30">
            <AvatarImage src={user?.img_perfil} alt={user?.nombre} />
            <AvatarFallback className="bg-primary/15 text-primary font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Link>
      </div>
    </header>
  )
}
